import React, { useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';

interface LoginFormProps {
  onSuccess?: () => void;
  className?: string;
}

export function LoginForm({ onSuccess, className = '' }: LoginFormProps) {
  const { signIn, signInWithEmail, createUserWithEmail, error, isLoading, clearError } = useAuth();
  
  const [mode, setMode] = useState<'google' | 'email'>('google');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const resetErrors = useCallback(() => {
    setLocalError(null);
    clearError();
  }, [clearError]);

  const handleGoogleSignIn = useCallback(async () => {
    try {
      resetErrors();
      await signIn();
      onSuccess?.();
    } catch (err: any) {
      setLocalError(err.message);
    }
  }, [signIn, onSuccess, resetErrors]);

  const submitEmailLogin = useCallback(async (action: 'signin' | 'signup') => {
    try {
      resetErrors();
      
      if (action === 'signin') {
        await signInWithEmail(email, password);
      } else {
        await createUserWithEmail(email, password);
      }

      onSuccess?.();
    } catch (err: any) {
      setLocalError(err.message);
    }
  }, [email, password, signInWithEmail, createUserWithEmail, onSuccess, resetErrors]);

  const disabled = isLoading || (!email || !password);
  const displayError = localError || error || null;

  return (
    <div className={`w-full max-w-md mx-auto ${className}`}>
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        <header className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
          <p className="text-gray-600">Access your BuildGravity account</p>
        </header>

        <div className="flex bg-gray-100 rounded-md p-1">
          <button
            type="button"
            onClick={() => setMode('google')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'google' 
                ? 'bg-white shadow-sm text-gray-900' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Google
          </button>
          <button
            type="button"
            onClick={() => setMode('email')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'email' 
                ? 'bg-white shadow-sm text-gray-900' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Email
          </button>
        </div>

        {displayError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {displayError}
          </div>
        )}

        {mode === 'google' ? (
          <div className="space-y-4">
            <button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </div>
              )}
            </button>

            <div className="text-center text-sm text-gray-600">
              <p>Sign in with your organization account</p>
            </div>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={async (event) => {
              event.preventDefault();
              await submitEmailLogin('signin');
            }}
          >
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input
                id="email"
                data-testid="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <input
                id="password"
                data-testid="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your password"
                required
              />
            </div>

            <div className="space-y-2">
              <button
                type="submit"
                data-testid="sign-in"
                disabled={disabled}
                className="w-full flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Signing in…' : 'Sign in with Email'}
              </button>

              <button
                type="button"
                onClick={() => submitEmailLogin('signup')}
                disabled={disabled}
                className="w-full flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Create an account
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}